import React, { useEffect, useState, useMemo } from 'react';
import './PortraitPicker.css';

/**
 * PortraitPicker
 *
 * Reusable portrait selection grid. Fetches the catalog from /api/portraits
 * and renders thumbnails grouped by race, with the player's race surfaced
 * first.
 *
 * Props:
 *   race          (string)   currently-selected race; that bucket is shown
 *                            first and as the default tab
 *   selected      (string)   currently-selected portrait URL (or null)
 *   onSelect      (function) called with new portrait URL when a thumbnail
 *                            is clicked. Pass null to clear selection.
 *   compact       (bool)     smaller thumbnails (used in modal grids)
 *   showClear     (bool)     show a "use default" tile to clear the override
 */
const PortraitPicker = ({ race, selected, onSelect, compact = false, showClear = false }) => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(race || 'Human');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch('/api/portraits')
      .then(r => {
        if (!r.ok) throw new Error('Failed to load portrait catalog');
        return r.json();
      })
      .then(data => { if (!cancelled) { setCatalog(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Reset active tab when race changes
  useEffect(() => {
    if (race) setActiveTab(race);
  }, [race]);

  // Tab order: player race first, then the other races, then "Shared"
  const tabs = useMemo(() => {
    if (!catalog) return [];
    const races = Object.keys(catalog.byRace || {});
    const order = race && races.includes(race)
      ? [race, ...races.filter(r => r !== race)]
      : races.slice();
    if (catalog.shared && catalog.shared.length > 0) order.push('__shared__');
    return order;
  }, [catalog, race]);

  if (loading) return <div className="pp-loading">Loading portraits…</div>;
  if (error) return <div className="pp-error">⚠ {error}</div>;
  if (!catalog) return null;

  const activeList = activeTab === '__shared__'
    ? (catalog.shared || [])
    : (catalog.byRace[activeTab] || []);

  return (
    <div className={'portrait-picker' + (compact ? ' pp-compact' : '')}>
      <div className="pp-tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            type="button"
            className={'pp-tab' + (activeTab === tab ? ' pp-tab-active' : '')}
            onClick={() => setActiveTab(tab)}
          >
            {tab === '__shared__' ? 'Shared' : tab}
            {tab === race ? ' ★' : ''}
          </button>
        ))}
      </div>

      {activeList.length === 0 ? (
        <div className="pp-empty">
          No portraits available yet for <b>{activeTab === '__shared__' ? 'shared' : activeTab}</b>.
          {' '}Drop PNGs into <code>client/public/images/character_portraits/{activeTab === '__shared__' ? '' : activeTab.replace(/[^A-Za-z0-9]+/g, '_').replace(/^Half_Orc$/, 'Half_orc')}/</code> and they'll appear here automatically.
        </div>
      ) : (
        <div className="pp-grid">
          {showClear && (
            <button
              type="button"
              className={'pp-tile pp-tile-clear' + (!selected ? ' pp-tile-selected' : '')}
              onClick={() => onSelect(null)}
              title="Use the default portrait"
            >
              <div className="pp-tile-clear-inner">Default</div>
            </button>
          )}
          {activeList.map(url => (
            <button
              key={url}
              type="button"
              className={'pp-tile' + (selected === url ? ' pp-tile-selected' : '')}
              onClick={() => onSelect(url)}
              title={url.split('/').pop()}
            >
              <img src={url} alt="" loading="lazy" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortraitPicker;
